import { Link, useLocation } from 'react-router-dom'
import {
	EuiEmptyPrompt,
	EuiFlexGroup,
	EuiFlexItem,
	EuiLoadingSpinner,
	EuiSpacer,
	EuiTable,
	EuiTableBody,
	EuiTableHeader,
	EuiTableHeaderCell,
	EuiTableRow,
	EuiTableRowCell,
	EuiText,
} from '@equipmentshare/ds2'
import { DemandRequestsQuery, DemandRequestStatus, DemandRequestType, Permission, useDemandRequestsQuery } from '../../../../graphql'
import { DemandRequestAssignmentStatus, DemandRequestStatusBadge } from '../../utils/demandRequestStatus'
import RequestActionsMenu from '../RequestActionsMenu'
import CursorPagination from '../../../components/CursorPagination'
import usePermissions from '../../../../hooks/usePermissions'
import { checkPermissions } from '../../../../components/PermissionsCheck/helpers'
import { RequestsListPaging } from '../../hooks/useRequestsListParams'

type DemandRequest = NonNullable<DemandRequestsQuery['demandRequests']['edges'][0]>['node']

const POLL_INTERVAL_MS = 60000 // 1 minute

function formatFulfillmentOrders( lineItems: DemandRequest['lineItems'] ): string {
	const orders = new Map<string, string>()
	lineItems.forEach( ( item ) => {
		item.fulfillmentOrderLineItems.forEach( ( { fulfillmentOrder } ) => {
			orders.set( fulfillmentOrder.id, `FO-${fulfillmentOrder.orderNumber}` )
		} )
	} )
	return [ ...orders.values() ].join( ', ' ) || '—'
}

export type DemandRequestsListProps = {
	/** Optional filter by fulfilling inventory IDs */
	fulfillingInventoryIds?: string[],
	/** Optional filter by requesting inventory IDs */
	requestingInventoryIds?: string[],
	/** Optional filter by statuses */
	statuses?: DemandRequestStatus[],
	/** Optional filter by assigned-to user IDs */
	assignedToIds?: string[],
	/** Optional filter by requested-by user IDs */
	requestedByUserIds?: string[],
	/** Optional filter by request numbers */
	requestNumbers?: number[],
	/** Decimal strings preserve BigInteger precision when sent as GraphQL variables. */
	fulfillmentOrderNumbers?: string[],
	/** Optional filter by part IDs FOR NOW WE ONLY ALLOW A SINGLE PART ID FROM THE PICKER */
	partIds?: string[],
	/** Cursor window to fetch. Owned by the parent so it can live in the URL. */
	paging: RequestsListPaging,
	/** Rows per page, reflected in the pagination footer. */
	rowSize: number,
	/** True when no cursor is applied — lets an action refresh in place instead of re-paging. */
	isFirstPage: boolean,
	/** True when at least one filter is active, which changes the empty-state copy. */
	hasAnyFilter: boolean,
	pageForward: ( cursor: string ) => () => void,
	pageBackward: ( cursor: string ) => () => void,
	setRowSize: ( rowSize: number ) => void,
	resetPaging: () => void,
}

export default function DemandRequestsList( {
	fulfillingInventoryIds,
	requestingInventoryIds,
	statuses,
	assignedToIds,
	requestedByUserIds,
	requestNumbers,
	fulfillmentOrderNumbers,
	partIds,
	paging,
	rowSize,
	isFirstPage,
	hasAnyFilter,
	pageForward,
	pageBackward,
	setRowSize,
	resetPaging,
}: DemandRequestsListProps ) {
	// Check permissions for editing demand requests
	const { userPermissions } = usePermissions( [ Permission.CanEditDemandRequestByStoreId ] )
	const canUserEditRequests = checkPermissions( userPermissions, [ Permission.CanEditDemandRequestByStoreId ] )

	// Handed to the request detail so its breadcrumb can return to this exact
	// list — filters, page and row size all live in the query string.
	const location = useLocation()

	// Query demand requests. `cache-and-network` paints the previous rows immediately
	// on back-navigation and refreshes them behind the scenes, so returning from a
	// request detail no longer blanks the table out to a spinner.
	const { data, loading, error, refetch } = useDemandRequestsQuery( {
		variables: {
			requestingInventoryIds: requestingInventoryIds && requestingInventoryIds.length > 0 ? requestingInventoryIds : undefined,
			fulfillingInventoryIds: fulfillingInventoryIds ?? undefined,
			statuses: statuses && statuses.length > 0 ? statuses : undefined,
			assignedToIds: assignedToIds && assignedToIds.length > 0 ? assignedToIds : undefined,
			requestedByUserIds: requestedByUserIds && requestedByUserIds.length > 0 ? requestedByUserIds : undefined,
			requestNumbers: requestNumbers && requestNumbers.length > 0 ? requestNumbers : undefined,
			fulfillmentOrderNumbers: fulfillmentOrderNumbers && fulfillmentOrderNumbers.length > 0 ? fulfillmentOrderNumbers : undefined,
			partIds: partIds && partIds.length > 0 ? partIds : undefined,
			// The Requests view lists stock requests only
			requestTypes: [ DemandRequestType.Stock ],
			paging,
		},
		// `notifyOnNetworkStatusChange` is deliberately left off: `cache-and-network`
		// paints from the cache without it, and turning it on flips `loading` on
		// every poll — which would swap the "No requests found" prompt for a spinner
		// once a minute on any list that legitimately has no matches.
		fetchPolicy: 'cache-and-network',
		pollInterval: POLL_INTERVAL_MS,
	} )

	// Clearing the cursor re-runs the query through the new variables, so only
	// refetch explicitly when we are already on the first page and the variables
	// would not change. Calling both would fire two requests for one action.
	const handleActionComplete = async() => {
		if ( isFirstPage ) {
			await refetch()
			return
		}
		resetPaging()
	}

	const demandRequests = data?.demandRequests?.edges?.map( ( edge ) => edge.node ) ?? []

	const getTotalQuantity = ( lineItems: DemandRequest['lineItems'] ) => {
		if ( !lineItems || lineItems.length === 0 ) {
			return 0
		}
		return lineItems.reduce( ( sum, item ) => sum + item.quantityRequested, 0 )
	}

	const formatDate = ( dateString: string ) => {
		const date = new Date( dateString )
		return date.toLocaleString( 'en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			hour12: true
		} )
	}

	const LoadingSpinner = () => (
		<EuiFlexGroup justifyContent="spaceAround">
			<EuiFlexItem grow={false}>
				<EuiLoadingSpinner size="xl" />
			</EuiFlexItem>
		</EuiFlexGroup>
	)

	if ( loading && demandRequests.length === 0 ) {
		return <LoadingSpinner />
	}

	const ErrorPrompt = () => (
		<EuiEmptyPrompt
			color="danger"
			iconType="alert"
			title={<h2>Error loading requests</h2>}
			body={<p>{error?.message}</p>}
		/>
	)

	const EmptyPrompt = () => (
		<EuiEmptyPrompt
			iconType="documents"
			title={<h2>No requests found</h2>}
			body={
				<p>
					{hasAnyFilter || fulfillingInventoryIds
						? 'There are no requests matching the selected filters.'
						: 'Select filters to view requests.'
					}
				</p>
			}
		/>
	)

	if ( error ) {
		return <ErrorPrompt />
	}

	if ( demandRequests.length === 0 ) {
		return <EmptyPrompt />
	}

	return (
		<>
			<EuiTable tableLayout="auto">
				<EuiTableHeader>
					<EuiTableHeaderCell>Request #</EuiTableHeaderCell>
					<EuiTableHeaderCell>FO-#</EuiTableHeaderCell>
					<EuiTableHeaderCell>Store</EuiTableHeaderCell>
					<EuiTableHeaderCell>Requested By</EuiTableHeaderCell>
					<EuiTableHeaderCell>Assigned To</EuiTableHeaderCell>
					<EuiTableHeaderCell>Total Qty</EuiTableHeaderCell>
					<EuiTableHeaderCell>Date Submitted</EuiTableHeaderCell>
					<EuiTableHeaderCell>Status</EuiTableHeaderCell>
					<EuiTableHeaderCell align="right">Actions</EuiTableHeaderCell>
				</EuiTableHeader>

				<EuiTableBody>
					{demandRequests.map( ( request, index ) => (
						<EuiTableRow key={request.id || `${request.companyId}-${request.requestingInventoryId}-${request.fulfillingInventoryId}-${index}`}>
							<EuiTableRowCell>
								<Link
									to={`/requests/${request.id}`}
									state={{ from: `/requests${location.search}` }}
									className="euiLink"
								>
									REQ-{request.requestNumber}
								</Link>
							</EuiTableRowCell>
							<EuiTableRowCell>
								<EuiText size="s">
									{formatFulfillmentOrders( request.lineItems )}
								</EuiText>
							</EuiTableRowCell>
							<EuiTableRowCell>
								<EuiText size="s">
									{request.requestingInventory?.name || 'Unknown'}
								</EuiText>
							</EuiTableRowCell>
							<EuiTableRowCell>
								<EuiText size="s">
									{`${request.requestedByUser.firstName} ${request.requestedByUser.lastName}`}
								</EuiText>
							</EuiTableRowCell>
							<EuiTableRowCell>
								<EuiText size="s">
									<DemandRequestAssignmentStatus request={request} />
								</EuiText>
							</EuiTableRowCell>
							<EuiTableRowCell>
								<EuiText size="s">
									{getTotalQuantity( request.lineItems )}
								</EuiText>
							</EuiTableRowCell>
							<EuiTableRowCell>
								<EuiText size="s">
									{request.dateCreated ? formatDate( request.dateCreated ) : '—'}
								</EuiText>
							</EuiTableRowCell>
							<EuiTableRowCell style={{ whiteSpace: 'nowrap' }}>
								<DemandRequestStatusBadge request={request} />
							</EuiTableRowCell>
							<EuiTableRowCell align="right">
								<RequestActionsMenu
									request={request}
									canUserEditRequests={canUserEditRequests}
									onActionComplete={handleActionComplete}
								/>
							</EuiTableRowCell>
						</EuiTableRow>
					) )}
				</EuiTableBody>
			</EuiTable>

			{data?.demandRequests?.pageInfo && (
				<>
					<EuiSpacer size="m" />
					<CursorPagination
						pageInfo={data.demandRequests.pageInfo}
						pageForward={pageForward}
						pageBackward={pageBackward}
						rowSize={rowSize}
						setRowSize={setRowSize}
					/>
				</>
			)}
		</>
	)
}
